import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

export type LosIgdKategoriTarget = "merah" | "kuning" | "hijau";

export type LosIgdKategori = LosIgdKategoriTarget | "hitam" | "tanpa_kategori";

export type LosIgdHasil = "sesuai" | "melebihi" | "tidak_dihitung";

export type LosIgdAlasan =
  | "hitam"
  | "batal"
  | "tanpa_kategori"
  | "jam_masuk_kosong"
  | "belum_keluar";

export type LosIgdDasarKeluar =
  | "resume_ralan"
  | "sampai_bangsal"
  | "waktu_meninggal"
  | "dout"
  | "bukti_tak_langsung";

export interface LosIgdRow {
  register_id: number;
  tgl: string;
  rm: string;
  nama: string;
  triage_id: number | null;
  doa: boolean;
  kategori: LosIgdKategori;
  kategori_label: string;
  jam_masuk: string | null;
  jam_keluar: string | null;
  dasar_keluar: LosIgdDasarKeluar | null;
  dasar_keluar_label: string | null;
  disposisi: string | null;
  status: string;
  status_label: string;
  los_menit: number | null;
  target_menit: number | null;
  hasil: LosIgdHasil;
  hasil_label: string;
  alasan: LosIgdAlasan | null;
  alasan_label: string | null;
}

export interface LosIgdStatistik {
  jumlah: number;
  sesuai: number;
  melebihi: number;
  persen: number | null;
  rata_menit: number | null;
  median_menit: number | null;
  maks_menit: number | null;
}

export interface LosIgdKategoriInfo {
  label: string;
  prioritas: string;
  area: string;
  kondisi: string;
  target_menit: number;
}

export interface LosIgdRekapKategori
  extends LosIgdKategoriInfo,
    LosIgdStatistik {
  kode: LosIgdKategoriTarget;
}

export interface LosIgdRekap {
  kategori: LosIgdRekapKategori[];
  total: LosIgdStatistik;
  dikecualikan: Record<LosIgdAlasan, number>;
  jumlah_kunjungan: number;
}

export interface LosIgdPeriodeInfo {
  tahun: number;
  bulan: number;
  label: string;
  start: string;
  end: string;
}

export interface LosIgdResponse {
  rows: LosIgdRow[];
  rekap: LosIgdRekap;
  meta: {
    periode: LosIgdPeriodeInfo;
    dasar_keluar: Record<LosIgdDasarKeluar, number>;
  };
  referensi: {
    kategori: Record<LosIgdKategoriTarget, LosIgdKategoriInfo>;
    alasan: Record<LosIgdAlasan, string>;
    dasar_keluar: Record<LosIgdDasarKeluar, string>;
  };
  params: { max_pdf_baris: number };
}

export interface LosIgdPeriode {
  tahun: number;
  bulan: number;
}

function bacaBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

interface GalatHttp {
  response?: { data?: unknown };
}

async function tampilkanGalat(err: GalatHttp): Promise<never> {
  const data = err.response?.data;

  if (err.response && data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await bacaBlob(data));
    } catch {
      err.response.data = { message: "Berkas gagal dibuat" };
    }
  }

  ErrorAlert.showErrorAlert(err);

  return Promise.reject(err);
}

function suffix(p: LosIgdPeriode): string {
  return `${p.tahun}-${String(p.bulan).padStart(2, "0")}`;
}

async function get(p: LosIgdPeriode): Promise<LosIgdResponse> {
  const res = await http
    .getDataParam(`/laporan/los-igd`, { params: p })
    .catch(tampilkanGalat);

  return res.data.data;
}

async function openPdf(p: LosIgdPeriode): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/los-igd/pdf`, {
      params: p,
      responseType: "blob"
    })
    .catch(tampilkanGalat);

  const url = URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  );
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function downloadExcel(p: LosIgdPeriode): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/los-igd/excel`, {
      params: p,
      responseType: "blob"
    })
    .catch(tampilkanGalat);

  const url = URL.createObjectURL(
    new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `rekap-los-igd_${suffix(p)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function formatDurasi(menit: number | null): string {
  if (menit === null || menit === undefined) return "-";

  const bulat = Math.round(menit);
  const jam = Math.floor(bulat / 60);
  const sisa = bulat % 60;

  if (jam === 0) return `${sisa} mnt`;

  return sisa === 0 ? `${jam} jam` : `${jam} jam ${sisa} mnt`;
}

export default { get, openPdf, downloadExcel };
